import Panel from "components/panels/panel";
import React, { useEffect } from "react";
import { useManualPagination } from "utilities/hookutils";
import AdvancingRowCard from "advancingapprove/list/AdvancingRowCard";
import { NoDataAvailableDiv } from "components/NoDataAvailableDiv";
import { Loading } from "components/panels";
import FilterHeader from "components/filters/FilterHeader";
import OwnersFilter from "components/filters/Filters/OwnersFilter";
import PeriodFilter from "components/filters/Filters/PeriodFilter";
import { FilterProvider, type FilterProviderRef, type FilterValues } from "components/filters/FilterContext";
import { mapOwnersFilterToInstruction, mapPeriodFilterToInstruction, mapResponseToOwnerFilter, mapResponseToPeriodFilter } from "components/filters/mappingUtils";
interface AdvancingListDashboardProps {
  config: server.dto.DashboardAdvancingApprovePanelConfig
}

export const AdvancingListDashboard = (props: AdvancingListDashboardProps) => {
  const filterRef = React.useRef<FilterProviderRef>(null);
  const maxPerPage = 10;
  const [isLoading, setIsLoading] = React.useState(true);
  const [advancings, setAdvancings] = React.useState<server.dto.AdvancingSessionForListForInternalUser[]>([]);

  const [instruction, setInstruction] = React.useState<server.dto.PaginatedAdvancingSessionInstruction>({
    ...props.config.initialInstruction,
    page: 1,
    perPage: maxPerPage,
    cache: "dashboardAdvancing"
  });

  const fetchAdvancing = async (instruction: server.dto.PaginatedAdvancingSessionInstruction) => {
    setIsLoading(true);
    const res = await window.CoreApi.AdvancingSession.Paginated(instruction);
    setIsLoading(false);
    setAdvancings(res.data.data ?? []);
    pagination.setPagination(res.data.pagination);

    filterRef.current.updateValues((_: FilterValues) => {
      return {
        period: mapResponseToPeriodFilter(res.data.request.period),
        owners: mapResponseToOwnerFilter(res.data.request.ownerUsers)
      };
    });

    return res;
  };

  const onFilterChange = (filter: FilterValues) => {
    setInstruction(mapToInstruction(filter));
  };

  const paginate = (paginationData: Partial<server.dto.PaginationData>) => {
    setInstruction({
      ...instruction,
      page: paginationData.page,
      perPage: paginationData.perPage
    });
  };

  const pagination = useManualPagination(instruction.page || 1, maxPerPage, paginate);

  const mapToInstruction = (filter: FilterValues): server.dto.PaginatedAdvancingSessionInstruction => {
    return {
      period: mapPeriodFilterToInstruction(filter.period),
      ownerUserIds: mapOwnersFilterToInstruction(filter.owners),
      page: 1,
      perPage: pagination.perPage,
      cache: "dashboardAdvancing"
    } satisfies server.dto.PaginatedAdvancingSessionInstruction;
  };

  const isInitialRequest = React.useRef(true);
  useEffect(() => {
    fetchAdvancing(instruction)
      .then(() => {
        if (!isInitialRequest.current) {
          return;
        }
        isInitialRequest.current = false;
        filterRef.current.setDefaultValues(() => {
          return {
            period: mapResponseToPeriodFilter(props.config.defaultFilters.period),
            owners: mapResponseToOwnerFilter(props.config.defaultFilters.ownerUsers)
          };
        });
      });
  }, [instruction]);

  return <Panel
    panelClassName="s1_dashboard-panel"
    title={props.config.headerLabel}
    articleId={serverReference.supportArticleIds.whatIsAdvancing}
    contentCssClass="flex-column gap-large"
  >
    <FilterProvider ref={filterRef} onFilterChange={onFilterChange}>
      <FilterHeader.Filters compact>
        <div className="selectiontool-header-filters">
          <OwnersFilter title={RESX.SoUser.User} />
          <PeriodFilter />
        </div>
      </FilterHeader.Filters>

      {!isLoading && advancings.length === 0 && <NoDataAvailableDiv />}

      {isLoading && <Loading />}

      {!isLoading && advancings.length > 0 && <div className="flex justify-end">
        {pagination.Component}
      </div>}

      {!isLoading && advancings.length > 0 &&
        <div className="advancing-session-list">
          {advancings.map(row => (
            <AdvancingRowCard key={row.advancingSession.advancingSessionId} item={row} />
          ))}
        </div>}
    </FilterProvider>
  </Panel>;
};
